import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin, Sprout, MessageCircle } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { getConsent, setConsent } from "@/lib/tracking";
import { waLink, imgUrl } from "@/lib/api";

const NAV = [["/", "Home"], ["/how-it-works", "How It Works"], ["/products", "Mowers"], ["/faq", "FAQ"], ["/contact", "Contact"]];

export function Logo({ light }) {
  const { settings } = useSettings();
  return (
    <Link to="/" data-testid="site-logo" className="flex items-center gap-2.5">
      {settings.logo_url ? (
        <img src={imgUrl(settings.logo_url)} alt={settings.business_name} className="h-9 w-auto" />
      ) : (
        <span className="grid h-9 w-9 place-items-center rounded-lg bg-emerald-700 text-white"><Sprout className="h-5 w-5" /></span>
      )}
      <span className={`font-display text-lg font-extrabold tracking-tight ${light ? "text-white" : "text-slate-900"}`}>{settings.business_name}</span>
    </Link>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const { settings } = useSettings();
  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo />
        <nav className="hidden items-center gap-1 lg:flex">
          {NAV.map(([to, l]) => (
            <NavLink key={to} to={to} end data-testid={`nav-link-${l.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) => `rounded-full px-4 py-2 text-[15px] font-medium transition-colors ${isActive ? "text-emerald-800 bg-emerald-50" : "text-slate-700 hover:text-emerald-800"}`}>{l}</NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          {settings.phone && <a href={`tel:${settings.phone.replace(/\D/g, "")}`} data-testid="header-phone-link" className="hidden items-center gap-2 text-sm font-semibold text-slate-700 hover:text-emerald-800 xl:flex"><Phone className="h-4 w-4" />{settings.phone}</a>}
          <Link to="/book" data-testid="header-book-trial-button" className="hidden h-11 items-center rounded-full bg-emerald-700 px-5 text-sm font-bold text-white transition-colors hover:bg-emerald-800 sm:inline-flex">Book My ${Number(settings.trial_fee)} Trial</Link>
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="grid h-11 w-11 place-items-center rounded-full border border-slate-200 lg:hidden" aria-label="Menu">{open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</button>
        </div>
      </div>
      {open && (
        <div className="border-t border-slate-200 bg-white px-4 pb-6 pt-2 lg:hidden" data-testid="mobile-menu">
          {NAV.map(([to, l]) => <Link key={to} to={to} onClick={() => setOpen(false)} className="block border-b border-slate-100 py-3.5 text-base font-medium text-slate-800">{l}</Link>)}
          <Link to="/book" onClick={() => setOpen(false)} data-testid="mobile-book-trial-button" className="mt-4 flex h-12 items-center justify-center rounded-full bg-emerald-700 font-bold text-white">Book My ${Number(settings.trial_fee)} Trial</Link>
        </div>
      )}
    </header>
  );
}

function Footer() {
  const { settings } = useSettings();
  const col = "text-sm text-slate-400 transition-colors hover:text-white";
  return (
    <footer className="bg-[#0B130E] text-slate-300">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 md:grid-cols-12 lg:px-8">
        <div className="md:col-span-5">
          <Logo light />
          <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-400">{settings.tagline || "Try a lawn mower on your own lawn before you buy."}</p>
          <div className="mt-6 space-y-2.5 text-sm">
            {settings.phone && <p className="flex items-center gap-2.5"><Phone className="h-4 w-4 text-emerald-500" />{settings.phone}</p>}
            {settings.email && <p className="flex items-center gap-2.5"><Mail className="h-4 w-4 text-emerald-500" />{settings.email}</p>}
            {settings.address && <p className="flex items-center gap-2.5"><MapPin className="h-4 w-4 text-emerald-500" />{settings.address}</p>}
          </div>
        </div>
        <div className="md:col-span-3">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-emerald-500">Explore</p>
          <div className="flex flex-col gap-2.5">
            {[...NAV.slice(1), ["/book", "Book a Trial"]].map(([to, l]) => <Link key={to} to={to} className={col}>{l}</Link>)}
          </div>
        </div>
        <div className="md:col-span-4">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-emerald-500">Policies</p>
          <div className="flex flex-col gap-2.5">
            <Link to="/privacy-policy" data-testid="footer-privacy-link" className={col}>Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className={col}>Terms and Conditions</Link>
            <Link to="/trial-return-policy" data-testid="footer-trial-policy-link" className={col}>Trial and Return Policy</Link>
            <Link to="/contact#privacy-request" className={col}>Privacy / Data Deletion Request</Link>
            <button onClick={() => { localStorage.removeItem("lt_consent"); window.location.reload(); }} className={`${col} text-left`} data-testid="footer-cookie-settings">Cookie settings</button>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-6 text-xs text-slate-500 sm:flex-row sm:justify-between sm:px-6 lg:px-8">
          <p>© {new Date().getFullYear()} {settings.business_name}. All rights reserved.</p>
          <Link to="/admin" className="hover:text-slate-300" data-testid="footer-staff-login">Staff login</Link>
        </div>
      </div>
    </footer>
  );
}

function WhatsAppButton() {
  const { settings } = useSettings();
  if (!settings.whatsapp_number) return null;
  return (
    <a href={waLink(settings.whatsapp_number, settings.whatsapp_message)} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-chat-button"
      className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-3 font-semibold text-white shadow-2xl shadow-black/20 transition-transform duration-300 hover:scale-105">
      <MessageCircle className="h-5 w-5" /> <span className="hidden sm:inline">Chat on WhatsApp</span>
    </a>
  );
}

function CookieBanner() {
  const { settings } = useSettings();
  const [shown, setShown] = useState(!getConsent());
  if (!shown) return null;
  const choose = (v) => { setConsent(v, settings); setShown(false); };
  return (
    <div className="fixed inset-x-3 bottom-3 z-[60] mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white/95 p-5 shadow-2xl backdrop-blur-xl sm:inset-x-6" data-testid="cookie-banner">
      <p className="text-sm text-slate-700">We use optional analytics and advertising cookies to measure our marketing. They are only enabled if you accept. See our <Link to="/privacy-policy" className="font-semibold text-emerald-800 underline">Privacy Policy</Link>.</p>
      <div className="mt-4 flex gap-2">
        <button onClick={() => choose("granted")} data-testid="cookie-accept-button" className="h-10 rounded-full bg-emerald-700 px-5 text-sm font-semibold text-white hover:bg-emerald-800">Accept</button>
        <button onClick={() => choose("denied")} data-testid="cookie-decline-button" className="h-10 rounded-full border border-slate-300 px-5 text-sm font-semibold text-slate-800 hover:bg-slate-50">Decline</button>
      </div>
    </div>
  );
}

export default function SiteLayout() {
  const { pathname } = useLocation();
  return (
    <div className="flex min-h-screen flex-col bg-white font-body text-slate-800" key={pathname.split("/")[1]}>
      <Header />
      <main className="flex-1"><Outlet /></main>
      <Footer />
      <WhatsAppButton />
      <CookieBanner />
    </div>
  );
}
